import{SUPABASE_URL,SUPABASE_PUBLISHABLE_KEY,FALLBACK_RULES}from'./config.js';

const factory=globalThis.supabase?.createClient;
if(typeof factory!=='function')throw new Error('supabase_js_not_loaded');

const SESSION_STORAGE_KEY='idlemon_client_session';

function readOrCreateClientSessionId(){
  try{
    const existing=sessionStorage.getItem(SESSION_STORAGE_KEY);
    if(existing&&/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(existing)){
      return existing;
    }
  }catch{}
  const next=crypto.randomUUID();
  try{sessionStorage.setItem(SESSION_STORAGE_KEY,next)}catch{}
  return next;
}

let clientSessionId=readOrCreateClientSessionId();
let sessionClaimed=false;
// Cache do token: no pagehide não dá tempo de await getSession().
let cachedAccessToken='';
let sessionWatchTimer=null;
let supersededHandler=null;
let kickInFlight=false;
let pendingPasswordRecoverySession=null;

export const supabaseClient=factory(SUPABASE_URL,SUPABASE_PUBLISHABLE_KEY,{
  auth:{
    // O IdleMon e um SPA estatico: o retorno implicito nao depende do code
    // verifier guardado no navegador que solicitou o e-mail.
    flowType:'implicit',
    persistSession:true,
    autoRefreshToken:true,
    detectSessionInUrl:true,
  },
  global:{
    headers:{
      'x-idlemon-session':clientSessionId,
    },
    fetch:async(input,init)=>{
      const response=await fetch(input,init);
      if(!sessionClaimed)return response;
      try{
        const clone=response.clone();
        const payload=await clone.json();
        const message=String(payload?.message||'');
        if(message==='session_superseded'||message==='session_required'){
          forceLocalSessionKick(message);
        }
      }catch{}
      return response;
    },
  },
});

supabaseClient.auth.onAuthStateChange((event,session)=>{
  cachedAccessToken=session?.access_token||'';
  // O cliente começa a processar callbacks PKCE assim que este módulo é
  // importado. A UI da conta só é montada depois do bootstrap do jogo, então
  // preservamos o evento de recuperação para que um listener tardio não o perca.
  if(event==='PASSWORD_RECOVERY')pendingPasswordRecoverySession=session;
  else if(event==='SIGNED_OUT')pendingPasswordRecoverySession=null;
});

export function getClientSessionId(){
  return clientSessionId;
}

// Carimba a saída armada pelo botão "descansar". Usa fetch keepalive para
// sobreviver ao fechamento imediato da aba. Sem este carimbo o claim offline
// recusa com no_clean_exit — fechar o jogo sem descansar não paga nada.
export function markClientExitBeacon(exitAt=Date.now(),{reason='rest',atRisk=false}={}){
  if(!cachedAccessToken)return false;
  try{
    fetch(`${SUPABASE_URL}/rest/v1/rpc/mark_client_exit`,{
      method:'POST',
      keepalive:true,
      headers:{
        'Content-Type':'application/json',
        apikey:SUPABASE_PUBLISHABLE_KEY,
        Authorization:`Bearer ${cachedAccessToken}`,
        'x-idlemon-session':clientSessionId,
      },
      body:JSON.stringify({
        p_exit_at:new Date(exitAt).toISOString(),
        p_reason:String(reason||'rest').slice(0,40),
        p_at_risk:!!atRisk,
      }),
    }).catch(()=>{});
    return true;
  }catch{return false}
}

export function onClientSessionSuperseded(handler){
  supersededHandler=typeof handler==='function'?handler:null;
  return()=>{if(supersededHandler===handler)supersededHandler=null};
}

function isSessionError(error){
  const message=String(error?.message||error||'');
  return message.includes('session_superseded')
    ||message.includes('session_required')
    ||message.includes('authentication_required');
}

export async function handlePossibleSessionError(error){
  if(!isSessionError(error))return false;
  await forceLocalSessionKick(String(error?.message||error||'').includes('session_required')?'session_required':'session_superseded');
  return true;
}

async function forceLocalSessionKick(reason='session_superseded'){
  if(kickInFlight)return;
  kickInFlight=true;
  stopClientSessionWatch();
  sessionClaimed=false;
  try{await supabaseClient.auth.signOut({scope:'local'})}catch{}
  try{supersededHandler?.(reason)}catch(error){console.error('session kick handler failed',error)}
  kickInFlight=false;
}

export async function claimClientSession(){
  const session=await currentSession();
  if(!session){
    sessionClaimed=false;
    return null;
  }
  const{data,error}=await supabaseClient.rpc('claim_client_session',{
    p_session_id:clientSessionId,
  });
  if(error)throw error;
  sessionClaimed=Boolean(data?.ok);
  return data;
}

export async function getClientSessionState(){
  const session=await currentSession();
  if(!session){
    sessionClaimed=false;
    return null;
  }
  const{data,error}=await supabaseClient.rpc('get_client_session_state',{
    p_session_id:clientSessionId,
  });
  if(error)throw error;
  return data;
}

export async function takeoverClientSession(expectedGeneration){
  const session=await currentSession();
  if(!session){
    sessionClaimed=false;
    return null;
  }
  const generation=Number(expectedGeneration);
  if(!Number.isSafeInteger(generation)||generation<0)throw new Error('session_generation_required');
  const{data,error}=await supabaseClient.rpc('takeover_client_session',{
    p_session_id:clientSessionId,
    p_expected_generation:generation,
  });
  if(error)throw error;
  sessionClaimed=Boolean(data?.ok);
  return data;
}

export async function validateClientSession(){
  const{data,error}=await supabaseClient.rpc('validate_client_session',{
    p_session_id:clientSessionId,
  });
  if(error)throw error;
  if(data?.ok){
    sessionClaimed=true;
    return data;
  }
  await forceLocalSessionKick(data?.reason||'session_superseded');
  return data;
}

export function startClientSessionWatch({intervalMs=20000}={}){
  stopClientSessionWatch();
  const tick=()=>{
    if(document.visibilityState==='hidden')return;
    validateClientSession().catch(error=>{
      handlePossibleSessionError(error).catch(()=>{});
    });
  };
  sessionWatchTimer=setInterval(tick,Math.max(5000,Number(intervalMs)||20000));
  addEventListener('focus',tick);
  document.addEventListener('visibilitychange',tick);
  tick();
  return stopClientSessionWatch;
}

export function stopClientSessionWatch(){
  if(sessionWatchTimer){
    clearInterval(sessionWatchTimer);
    sessionWatchTimer=null;
  }
}

export async function currentSession(){
  const{data,error}=await supabaseClient.auth.getSession();
  if(error)throw error;
  return data.session||null;
}

export async function currentProfile(userId){
  const{data,error}=await supabaseClient
    .from('profiles')
    .select('user_id,trainer_name,rules_version,rules_accepted_at,created_at,inventory_migrated_at,progress_migrated_at,collection_migrated_at')
    .eq('user_id',userId)
    .maybeSingle();
  if(error)throw error;
  return data||null;
}

export async function currentWallet(userId){
  const{data,error}=await supabaseClient
    .from('wallets')
    .select('user_id,idlecoin,emerald,emerald_debt,last_sync_at,migration_completed_at')
    .eq('user_id',userId)
    .maybeSingle();
  if(error)throw error;
  return data||null;
}

export async function currentRules(){
  const{data,error}=await supabaseClient
    .from('market_config')
    .select('value')
    .eq('key','rules.current')
    .maybeSingle();
  if(error){
    console.warn('rules config unavailable; using bundled fallback',error);
    return structuredClone(FALLBACK_RULES);
  }
  return data?.value||structuredClone(FALLBACK_RULES);
}

export async function currentAdminContext(){
  const{data,error}=await supabaseClient.rpc('current_admin_context');
  if(error)throw error;
  return data&&typeof data==='object'?data:{is_admin:false,role:'player',capabilities:[]};
}

export async function trainerNameAvailable(trainerName){
  const{data,error}=await supabaseClient.rpc('trainer_name_available',{p_trainer_name:trainerName});
  if(error)throw error;
  return data===true;
}

export async function signupEmailStatus(email){
  const{data,error}=await supabaseClient.rpc('signup_email_status',{p_email:String(email||'').trim().toLowerCase()});
  if(error)throw error;
  return ['active','pending'].includes(data)?data:'available';
}

export async function createAccount({email,password,trainerName,rulesVersion,captchaToken}){
  const options={
    data:{trainer_name:trainerName,rules_version:rulesVersion},
    emailRedirectTo:authRedirectUrl('confirm'),
  };
  if(captchaToken)options.captchaToken=captchaToken;
  const{data,error}=await supabaseClient.auth.signUp({email,password,options});
  if(error)throw error;
  return data;
}

export async function resendSignupConfirmation({email,captchaToken}){
  const options={emailRedirectTo:authRedirectUrl('confirm')};
  if(captchaToken)options.captchaToken=captchaToken;
  const{error}=await supabaseClient.auth.resend({type:'signup',email,options});
  if(error)throw error;
}

// Fluxo de link próprio: o template de e-mail aponta para idlemon.com.br com
// token_hash, e a confirmação/recuperação acontece aqui em vez do /verify do
// Supabase. Assim o jogador nunca vê a URL do projeto.
export async function verifyEmailToken({tokenHash,type}){
  const{data,error}=await supabaseClient.auth.verifyOtp({token_hash:tokenHash,type});
  if(error)throw error;
  return data;
}

export async function signInAccount({identifier,password,captchaToken}){
  const value=String(identifier||'').trim();
  if(value.includes('@')){
    const options=captchaToken?{captchaToken}:undefined;
    const{data,error}=await supabaseClient.auth.signInWithPassword({email:value.toLowerCase(),password,options});
    if(error)throw error;
    return data;
  }
  const{data,error}=await supabaseClient.functions.invoke('login-with-trainer',{
    body:{trainer_name:value,password,captcha_token:captchaToken||undefined},
  });
  if(error){
    const status=error?.context?.status;
    if(status===400)throw new Error('invalid login credentials');
    if(status===429)throw new Error('over_request_rate_limit');
    // Fase F: sem fallback legacyTrainerLogin — só edge function / e-mail
    throw new Error('invalid login credentials');
  }
  if(!data?.session?.access_token)throw new Error('invalid login credentials');
  const{data:sessionData,error:sessionError}=await supabaseClient.auth.setSession({
    access_token:data.session.access_token,
    refresh_token:data.session.refresh_token,
  });
  if(sessionError)throw sessionError;
  return sessionData;
}

export async function requestPasswordReset(email,captchaToken){
  const options={redirectTo:authRedirectUrl('recovery')};
  if(captchaToken)options.captchaToken=captchaToken;
  const{error}=await supabaseClient.auth.resetPasswordForEmail(String(email||'').trim().toLowerCase(),options);
  if(error)throw error;
}

function authRedirectUrl(intent){
  const redirectUrl=new URL(`${location.origin}${location.pathname}`);
  redirectUrl.searchParams.set('auth',intent);
  return redirectUrl.toString();
}

export async function updateAccountPassword(password){
  const{data,error}=await supabaseClient.auth.updateUser({password});
  if(error)throw error;
  return data;
}

export async function ensureProfile({trainerName,rulesVersion,requestId=crypto.randomUUID()}){
  const{data,error}=await supabaseClient.rpc('ensure_profile',{
    p_trainer_name:trainerName,
    p_rules_version:rulesVersion,
    p_request_id:requestId,
  });
  if(error)throw error;
  return data;
}

export async function signOutAccount({scope='global'}={}){
  stopClientSessionWatch();
  sessionClaimed=false;
  const{error}=await supabaseClient.auth.signOut({scope});
  if(error)throw error;
}

export function onAccountChange(callback){
  return supabaseClient.auth.onAuthStateChange((event,session)=>callback(event,session));
}

export function consumePasswordRecoverySession(){
  const session=pendingPasswordRecoverySession;
  pendingPasswordRecoverySession=null;
  return session;
}

export{sessionClaimed};
